import * as THREE from 'three';
import { DEFAULT_EASING_FUNCTION, applyEasing, EASING_FUNCTION } from '../../../utils/EasingFunctions';

const MM_TO_UNIT = 0.1; // 1 mm = 0.1 cm (scene units)

/**
 * Smoothstep interpolation function (Hermite)
 * @param {number} edge0 - Lower edge
 * @param {number} edge1 - Upper edge
 * @param {number} x - Input value
 * @returns {number} Smoothstepped value [0, 1]
 */
export function smoothstep(edge0, edge1, x) {
  // Clamp x to [0, 1] range
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  // Hermite interpolation
  return t * t * (3 - 2 * t);
}

/**
 * Compute radial direction from centroid in XZ plane
 * @param {THREE.Vector3} vertexPos - Vertex position
 * @param {THREE.Vector3} centroid - Boundary centroid
 * @returns {THREE.Vector3} Normalized radial direction
 */
export function computeRadialDirection(vertexPos, centroid) {
  const dx = vertexPos.x - centroid.x;
  const dz = vertexPos.z - centroid.z;
  const length = Math.sqrt(dx * dx + dz * dz);

  if (length < 1e-6) {
    return new THREE.Vector3(1, 0, 0); // Fallback
  }

  return new THREE.Vector3(dx / length, 0, dz / length);
}

/**
 * Compute normal data for a vertex
 * @param {number} vertexIndex - Vertex index
 * @param {THREE.BufferAttribute} normalsAttr - Normals attribute
 * @param {THREE.Matrix4} matrixWorld - World matrix
 * @param {THREE.Vector3} centroid - Boundary centroid
 * @returns {Object} Normal data { surfaceNormal, radialNormal, verticalComponent }
 */
export function computeNormalData(vertexIndex, normalsAttr, matrixWorld, centroid) {
  const normal = new THREE.Vector3();
  normal.fromBufferAttribute(normalsAttr, vertexIndex);
  // Use proper normal matrix when converting to world space
  const normalMatrix = new THREE.Matrix3().getNormalMatrix(matrixWorld);
  normal.applyMatrix3(normalMatrix).normalize();

  // Project normal to XZ plane for radial direction
  const radialNormal = new THREE.Vector3(normal.x, 0, normal.z).normalize();

  return {
    surfaceNormal: normal,
    radialNormal: radialNormal,
    verticalComponent: normal.y
  };
}

/**
 * Compute Y-based falloff factor from boundary top
 * @param {number} vertexY - Vertex Y position
 * @param {number} boundaryTopY - Top Y position of boundary
 * @param {number} heightMm - Height parameter in mm
 * @returns {number} Falloff factor [0, 1] where 1 = at top, 0 = at height distance below
 */
export function computeYBasedFalloff(vertexY, boundaryTopY, heightMm) {
  if (heightMm <= 0) return 0;

  const heightUnits = heightMm * MM_TO_UNIT;
  const yDistance = boundaryTopY - vertexY; // Positive = below boundary

  if (yDistance <= 0) {
    // Vertex is at or above boundary - full influence
    return 1;
  }

  if (yDistance >= heightUnits) {
    // Vertex is beyond influence range
    return 0;
  }

  // Linear falloff from 1 (at top) to 0 (at height distance below)
  return 1 - (yDistance / heightUnits);
}

/**
 * Compute distance-based falloff from boundary using geodesic distance
 * @param {number} distanceUnits - Geodesic distance from boundary in scene units
 * @param {number} heightMm - Height parameter in mm (interpreted as max influence distance)
 * @returns {number} Falloff factor [0, 1] where 1 = at boundary, 0 = at max distance
 */
export function computeDistanceBasedFalloff(distanceUnits, heightMm) {
  if (heightMm <= 0) return 0;

  const maxDistance = heightMm * MM_TO_UNIT;
  if (distanceUnits <= 0) return 1;
  if (distanceUnits >= maxDistance) return 0;

  return 1 - (distanceUnits / maxDistance);
}

/**
 * Apply flare transformation to a vertex with Y-based falloff
 * @param {THREE.Vector3} originalPosition - Original vertex position
 * @param {number} boundaryTopY - Top Y position of boundary
 * @param {number} segmentIndex - Segment index (0-4)
 * @param {THREE.Vector3} deflectionDirection - Inherited deflection direction from nearest boundary vertex
 * @param {THREE.Vector3} centroid - Boundary centroid (unused, kept for compatibility)
 * @param {Object} flareParams - Flare parameters
 * @returns {THREE.Vector3} New vertex position
 */
export function applyFlareWithFalloff(
  originalPosition,
  boundaryTopY,
  segmentIndex,
  deflectionDirection,
  centroid,
  flareParams
) {
  const {
    segmentHeightValues,
    segmentDeflectValues
  } = flareParams;

  const heightMm = segmentHeightValues[segmentIndex];
  const deflectMm = segmentDeflectValues[segmentIndex];

  // Calculate Y-based falloff factor
  const falloffFactor = computeYBasedFalloff(originalPosition.y, boundaryTopY, heightMm);

  if (falloffFactor <= 0) {
    // Outside influence range, return unchanged
    return originalPosition.clone();
  }

  // Apply smoothing curve (ease-out cubic) for smoother transition
  const smoothFalloff = 1 - Math.pow(1 - falloffFactor, 3);

  // Get deflection in scene units, modulated by falloff
  const deflectUnits = deflectMm * MM_TO_UNIT * smoothFalloff;

  // Apply transformations
  const newPosition = originalPosition.clone();

  // Apply outward deflection using inherited direction from boundary curve
  // This ensures vertices below the same boundary point deflect consistently,
  // preventing overlap in concave regions
  newPosition.x += deflectionDirection.x * deflectUnits;
  newPosition.z += deflectionDirection.z * deflectUnits;

  return newPosition;
}

/**
 * Linear interpolation between two values
 * @param {number} a - Start value
 * @param {number} b - End value
 * @param {number} t - Interpolation factor [0, 1]
 * @returns {number} Interpolated value
 */
export function lerp(a, b, t) {
  return a + (b - a) * t;
}

/**
 * Apply flare transformation using geodesic distance-based falloff with smooth blending
 * This avoids artifacts in highly curved regions where Y-distance is misleading.
 * Uses inverted easing (ease-out-sine) for smooth interpolation.
 *
 * PRIMARY ZONE (0 → heightMm):
 *   - Continuously blends from deflectMm to falloffDeflectMm using ease-out-sine
 *
 * FALLOFF ZONE (heightMm → heightMm + falloffHeightMm) - with deflectBlendRatio:
 *   - Plateau (heightMm → heightMm + falloffHeightMm * deflectBlendRatio): Full falloff deflect value
 *   - Blend (heightMm + falloffHeightMm * deflectBlendRatio → heightMm + falloffHeightMm):
 *     Blends from falloffDeflectMm to 0 using ease-out-sine
 *
 * @param {THREE.Vector3} originalPosition - Original vertex position
 * @param {number} distanceFromBoundary - Geodesic distance in scene units
 * @param {number} segmentIndex - Segment index (0-4)
 * @param {THREE.Vector3} deflectionDirection - Outward deflection direction
 * @param {THREE.Vector3} centroid - Boundary centroid (unused)
 * @param {Object} flareParams - Flare parameters including deflectBlendRatio (0..1) for falloff zone only
 * @param {Object} [blendedParams] - Optional pre-computed blended parameters for angular smoothing
 *   {heightMm, deflectMm, falloffHeightMm, falloffDeflectMm}
 * @returns {THREE.Vector3} New vertex position
 */
export function applyFlareWithDistanceFalloff(
  originalPosition,
  distanceFromBoundary,
  segmentIndex,
  deflectionDirection,
  centroid,
  flareParams,
  blendedParams = null
) {
  const {
    segmentHeightValues,
    segmentDeflectValues,
    segmentFalloffHeightValues,
    segmentFalloffDeflectValues,
    deflectBlendRatio = 0.75 // Default: 75% keeps full deflect, 25% blends to 0
  } = flareParams;

  // Use blended parameters if provided (for angular smoothing), otherwise extract from segment arrays
  const heightMm = blendedParams?.heightMm ?? segmentHeightValues[segmentIndex];
  const deflectMm = blendedParams?.deflectMm ?? segmentDeflectValues[segmentIndex];
  const falloffHeightMm = blendedParams?.falloffHeightMm ?? (segmentFalloffHeightValues?.[segmentIndex] || 0);
  const falloffDeflectMm = blendedParams?.falloffDeflectMm ?? (segmentFalloffDeflectValues?.[segmentIndex] || 0);

  if (!isFinite(distanceFromBoundary)) return originalPosition.clone();

  // Calculate total influence range
  const totalHeightMm = heightMm + falloffHeightMm;
  const totalDistance = totalHeightMm * MM_TO_UNIT;

  // Special handling for zero-height segments:
  // Allow boundary vertices to receive deflection even when total height is 0
  const MIN_BOUNDARY_DISTANCE = 0.002; // ~0.02mm threshold
  const effectiveMaxDistance = totalHeightMm > 0 ? totalDistance : MIN_BOUNDARY_DISTANCE;

  if (distanceFromBoundary >= effectiveMaxDistance) return originalPosition.clone();

  let deflectUnits = 0;

  // Two-zone falloff calculation
  const primaryDistance = heightMm * MM_TO_UNIT;
  const blendRatioClamped = Math.max(0, Math.min(1, deflectBlendRatio));

  if (distanceFromBoundary <= primaryDistance) {
    // PRIMARY ZONE: Smoothly blend from deflectMm to falloffDeflectMm
    if (heightMm > 0) {
      const t = distanceFromBoundary / primaryDistance;

      // Apply ease-out-sine easing for smooth transition
      const easeT = applyEasing(t, EASING_FUNCTION.EASE_OUT_CUBIC);

      // Invert: start at full deflect (1), end at falloff deflect (0)
      const invEaseT = 1.0 - easeT;

      // Interpolate between deflectMm and falloffDeflectMm
      const deflection = lerp(falloffDeflectMm, deflectMm, invEaseT);
      deflectUnits = deflection * MM_TO_UNIT;
    } else {
      // No primary height, use full deflect at boundary
      deflectUnits = deflectMm * MM_TO_UNIT;
    }
  } else if (distanceFromBoundary <= totalDistance) {
    // FALLOFF ZONE: Apply same plateau-then-blend pattern to secondary deflect
    if (falloffHeightMm > 0) {
      const falloffRange = falloffHeightMm * MM_TO_UNIT;
      const falloffPlateau = falloffRange * blendRatioClamped; // 75% plateau at full falloff deflect
      const falloffDistance = distanceFromBoundary - primaryDistance;

      if (falloffDistance <= falloffPlateau) {
        // FALLOFF PLATEAU: Keep full falloffDeflect value
        deflectUnits = falloffDeflectMm * MM_TO_UNIT;
      } else {
        // FALLOFF BLEND: Blend from falloffDeflectMm to 0 using easing
        if (blendRatioClamped < 1.0) {
          const falloffBlendRange = falloffRange - falloffPlateau;
          const distanceIntoFalloffBlend = falloffDistance - falloffPlateau;
          const t = distanceIntoFalloffBlend / falloffBlendRange;

          // Apply ease-out-sine easing for smooth transition to 0
          const easeT = applyEasing(t, EASING_FUNCTION.EASE_OUT_SINE);

          // Invert: start at full falloff deflect (1), end at 0
          const invEaseT = 1.0 - easeT;

          // Interpolate from falloffDeflectMm to 0
          const deflection = falloffDeflectMm * invEaseT;
          deflectUnits = deflection * MM_TO_UNIT;
        } else {
          // Fallback: use smoothstep if ratio is 1.0
          const t = falloffDistance / falloffRange;
          const smoothT = t * t * (3.0 - 2.0 * t);
          const invSmoothT = 1.0 - smoothT;
          deflectUnits = falloffDeflectMm * invSmoothT * MM_TO_UNIT;
        }
      }
    }
  }

  const newPosition = originalPosition.clone();
  newPosition.x += deflectionDirection.x * deflectUnits;
  newPosition.z += deflectionDirection.z * deflectUnits;
  return newPosition;
}
